import { z } from 'zod';

import { getJson, postJson } from './client';

const calculatorInputSchema = z.object({
  _id: z.string(),
  legacyId: z.string(),
  name: z.string(),
  label: z.string().nullable().optional(),
  options: z.array(z.record(z.unknown())).nullable().optional()
});

const calculatorOutputSchema = z.object({
  _id: z.string(),
  legacyId: z.string(),
  name: z.string(),
  label: z.string().nullable().optional(),
  unit: z.string().nullable().optional(),
  isDefault: z.boolean().optional().nullable()
});

const calculatorQuestionSchema = z.object({
  _id: z.string(),
  legacyId: z.string(),
  questionKey: z.string().nullable().optional(),
  label: z.string().nullable().optional(),
  multiSelect: z.boolean().optional().nullable(),
  options: z.array(z.record(z.unknown())).default([])
});

const calculatorSectionSchema = z.object({
  _id: z.string(),
  legacyId: z.string(),
  sectionKey: z.string(),
  title: z.string(),
  questions: z.array(calculatorQuestionSchema).default([]),
  ad: z
    .object({
      title: z.string().optional().nullable(),
      imageUrl: z.string().optional().nullable(),
      imageAlt: z.string().optional().nullable(),
      redirectUrl: z.string().optional().nullable(),
      text: z.string().optional().nullable()
    })
    .optional()
    .nullable()
});

const calculatorSchema = z.object({
  _id: z.string(),
  id: z.string(),
  legacyId: z.string(),
  title: z.string(),
  description: z.string().nullable().optional(),
  inputs: z.array(calculatorInputSchema).default([]),
  outputs: z.array(calculatorOutputSchema).default([]),
  sections: z.array(calculatorSectionSchema).default([])
});

const _calculatorEnvelopeSchema = z.object({
  calculator: calculatorSchema
});

const calculationSchema = z.object({
  _id: z.string(),
  legacyId: z.string(),
  calculator: z.string().nullable().optional(),
  inputs: z.record(z.string()).nullable().optional(),
  responses: z.array(z.record(z.unknown())).nullable().optional(),
  results: z.record(z.number().nullable()).nullable().optional(),
  type: z.string().nullable().optional()
});

const calculationEnvelopeSchema = z.object({
  calculation: calculationSchema
});

export type CalculatorDto = z.infer<typeof calculatorSchema>;

export type CalculationDto = z.infer<typeof calculationSchema>;

// NOTE: fetchCalculator moved to @/lib/calculators (server-only direct service call)

export async function fetchCalculation(legacyId: string) {
  const payload = await getJson(`/calculation/${legacyId}`, {
    schema: calculationEnvelopeSchema
  });
  return payload.calculation;
}

export type SaveCalculationPayload = {
  calculator: string;
  email?: string;
  inputs?: Record<string, unknown>;
  responses?: Array<Record<string, unknown>>;
  results?: Record<string, unknown>;
  type?: string;
};

export async function createCalculation(payload: SaveCalculationPayload) {
  const response = await postJson('/calculation', payload, {
    schema: calculationEnvelopeSchema
  });
  return response.calculation;
}
